import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

const countries = [
  { flag: "🇦🇪", label: "Dubai", labelKey: "nav.dubai", path: "/dubai" },
  { flag: "🇸🇦", label: "Saudi Arabia", labelKey: "nav.saudi", path: "/saudi-arabia" },
  { flag: "🇶🇦", label: "Qatar", labelKey: "nav.qatar", path: "/qatar" },
  { flag: "🇰🇼", label: "Kuwait", labelKey: "nav.kuwait", path: "/kuwait" },
  { flag: "🇴🇲", label: "Oman", labelKey: "nav.oman", path: "/oman" },
  { flag: "🇧🇭", label: "Bahrain", labelKey: "nav.bahrain", path: "/bahrain" },
  { flag: "🇹🇷", label: "Turkey", labelKey: "nav.turkey", path: "/turkey" },
  { flag: "🇵🇰", label: "Pakistan", labelKey: "nav.pakistan", path: "/pakistan" },
  { flag: "🇮🇱", label: "Israel", labelKey: "nav.israel", path: "/israel" },
];

const MobileBottomNav = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const [countriesOpen, setCountriesOpen] = useState(false);

  // Close sheet on route change
  useEffect(() => {
    setCountriesOpen(false);
  }, [location.pathname]);

  // Lock body scroll when sheet is open
  useEffect(() => {
    if (countriesOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [countriesOpen]);

  const isActive = (path: string, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const isCountryActive = countries.some((c) => location.pathname === c.path);

  return (
    <>
      {/* Countries Bottom Sheet */}
      {countriesOpen && (
        <div className="fixed inset-0 z-50 md:hidden flex flex-col justify-end">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setCountriesOpen(false)}
          />

          {/* Sheet */}
          <div className="relative bg-white rounded-t-3xl shadow-2xl pb-safe z-10"
            style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 80px)" }}
          >
            {/* Handle bar */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-gray-200" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
              <div>
                <p className="text-sm font-bold text-gray-900">{t("nav.browseByCountry", "Browse by Country")}</p>
                <p className="text-xs text-gray-400 mt-0.5">{t("nav.selectCountry", "Select a country to explore factories")}</p>
              </div>
              <button
                onClick={() => setCountriesOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 cursor-pointer"
              >
                <i className="ri-close-line text-base" />
              </button>
            </div>

            {/* Country Grid */}
            <div className="grid grid-cols-3 gap-3 px-4 py-4">
              {countries.map((c) => {
                const active = location.pathname === c.path;
                return (
                  <Link
                    key={c.path}
                    to={c.path}
                    className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-2xl border transition-all duration-150 cursor-pointer ${
                      active
                        ? "bg-amber-50 border-amber-300"
                        : "bg-gray-50 border-gray-100 active:bg-amber-50"
                    }`}
                  >
                    <span className="text-2xl leading-none">{c.flag}</span>
                    <span className={`text-xs font-semibold text-center leading-tight ${active ? "text-amber-700" : "text-gray-700"}`}>
                      {t(c.labelKey, c.label)}
                    </span>
                    {active && <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />}
                  </Link>
                );
              })}
            </div>

            {/* Register Factory CTA */}
            <div className="px-4 pb-2">
              <Link
                to="/register-factory"
                className="flex items-center justify-center gap-2 w-full py-3.5 rounded-2xl bg-amber-500 text-white font-bold text-sm cursor-pointer"
              >
                <i className="ri-store-2-line text-base" />
                {t("nav.registerFactory", "Register Your Factory")}
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Nav Bar */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-white border-t border-gray-100"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="flex items-stretch h-16">

          {/* Home */}
          <Link
            to="/"
            className="flex-1 flex flex-col items-center justify-center gap-0.5 cursor-pointer transition-colors duration-200 relative"
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <i className={`text-xl transition-colors duration-200 ${isActive("/", true) ? "ri-home-5-fill text-amber-500" : "ri-home-5-line text-gray-400"}`} />
            </div>
            {isActive("/", true) && <span className="w-1 h-1 rounded-full bg-amber-500 absolute bottom-1.5" />}
            <span className={`text-xs font-medium ${isActive("/", true) ? "text-amber-500" : "text-gray-400"}`}>
              {t("nav.home", "Home")}
            </span>
          </Link>

          {/* Search */}
          <Link
            to="/search"
            className="flex-1 flex flex-col items-center justify-center gap-0.5 cursor-pointer transition-colors duration-200 relative"
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <i className={`text-xl transition-colors duration-200 ${isActive("/search") ? "ri-search-fill text-amber-500" : "ri-search-line text-gray-400"}`} />
            </div>
            {isActive("/search") && <span className="w-1 h-1 rounded-full bg-amber-500 absolute bottom-1.5" />}
            <span className={`text-xs font-medium ${isActive("/search") ? "text-amber-500" : "text-gray-400"}`}>
              {t("nav.search", "Search")}
            </span>
          </Link>

          {/* RFQ — raised center highlight */}
          <Link
            to="/rfq"
            className="flex-1 flex flex-col items-center justify-center gap-0.5 cursor-pointer relative"
          >
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center -mt-5 shadow-md transition-all duration-200 ${
                isActive("/rfq") ? "bg-amber-600" : "bg-amber-500"
              }`}
            >
              <i className={`${isActive("/rfq") ? "ri-file-list-3-fill" : "ri-file-list-3-line"} text-white text-xl`} />
            </div>
            <span className="text-xs font-semibold text-amber-600 -mt-0.5">
              {t("nav.rfq", "RFQ")}
            </span>
          </Link>

          {/* Countries */}
          <button
            onClick={() => setCountriesOpen((v) => !v)}
            className="flex-1 flex flex-col items-center justify-center gap-0.5 cursor-pointer transition-colors duration-200 relative bg-transparent border-0"
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <i className={`text-xl transition-colors duration-200 ${isCountryActive || countriesOpen ? "ri-map-pin-2-fill text-amber-500" : "ri-map-pin-2-line text-gray-400"}`} />
            </div>
            {(isCountryActive || countriesOpen) && <span className="w-1 h-1 rounded-full bg-amber-500 absolute bottom-1.5" />}
            <span className={`text-xs font-medium ${isCountryActive || countriesOpen ? "text-amber-500" : "text-gray-400"}`}>
              {t("nav.countries", "Countries")}
            </span>
          </button>

          {/* More / Register */}
          <Link
            to="/register-factory"
            className="flex-1 flex flex-col items-center justify-center gap-0.5 cursor-pointer transition-colors duration-200 relative"
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <i className={`text-xl transition-colors duration-200 ${isActive("/register-factory") ? "ri-store-2-fill text-amber-500" : "ri-store-2-line text-gray-400"}`} />
            </div>
            {isActive("/register-factory") && <span className="w-1 h-1 rounded-full bg-amber-500 absolute bottom-1.5" />}
            <span className={`text-xs font-medium ${isActive("/register-factory") ? "text-amber-500" : "text-gray-400"}`}>
              {t("nav.register", "Register")}
            </span>
          </Link>

        </div>
      </nav>
    </>
  );
};

export default MobileBottomNav;